import axios, { AxiosInstance } from 'axios';
import { User, Chat, Message, ChatGroup, ApiResponse } from '../types';

class ApiService {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: '/',
      headers: {
        'Content-Type': 'application/json',
      },
      withCredentials: true,
    });

    // Add JWT token to requests
    this.api.interceptors.request.use((config) => {
      // Token is now in HttpOnly cookie, no need to add manually
      // Browser sends it automatically with withCredentials: true
      return config;
    });

    // Handle 401 responses
    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          // Only redirect if not already on login page
          if (window.location.pathname !== '/login') {
            window.location.href = '/login';
          }
        }
        return Promise.reject(error);
      }
    );
  }

  // Auth
  async login(login: string, password: string): Promise<ApiResponse<User> & { message?: string }> {
    const response = await this.api.post('/api/auth/login', { login, password });
    // Token is now set in HttpOnly cookie by server, no need to store locally
    return response.data;
  }

  async logout(): Promise<ApiResponse> {
    const response = await this.api.post('/api/auth/logout');
    // Cookie is cleared by server on logout
    return response.data;
  }

  async getCurrentUser(): Promise<ApiResponse<User>> {
    const response = await this.api.get('/api/auth/current');
    const data = response.data;

    if (data && typeof data === 'object' && 'success' in data) {
      return data;
    }

    if (data && typeof data === 'object' && 'id' in data) {
      return { success: true, data: data as User } as ApiResponse<User>;
    }

    return { success: false } as ApiResponse<User>;
  }

  
  async check2FA(attemptId: string): Promise<ApiResponse<User> & { message?: string }> {
    const response = await this.api.get(`/api/auth/check_2fa/${attemptId}`);
    return response.data;
  }

  // Chats
  async getChats(): Promise<ApiResponse<Chat[]>> {
    const response = await this.api.get('/api/chats');
    return response.data;
  }

  async getChat(chatId: number): Promise<ApiResponse<Chat>> {
    const response = await this.api.get(`/api/chats/${chatId}`);
    return response.data;
  }

  async updateChatDetails(chatId: number, data: Partial<Chat>): Promise<ApiResponse> {
    const response = await this.api.post(`/api/chats/${chatId}/details`, data);
    return response.data;
  }

  async assignChat(chatId: number, userId: number | null): Promise<ApiResponse> {
    const response = await this.api.post(`/api/chats/${chatId}/assign`, { user_id: userId });
    return response.data;
  }

  async transferChat(chatId: number, userId: number): Promise<ApiResponse> {
    const response = await this.api.post(`/api/chats/${chatId}/transfer`, { user_id: userId });
    return response.data;
  }

  async pinChat(chatId: number): Promise<ApiResponse> {
    const response = await this.api.post(`/api/chats/${chatId}/pin`);
    return response.data;
  }

  async unpinChat(chatId: number): Promise<ApiResponse> {
    const response = await this.api.post(`/api/chats/${chatId}/unpin`);
    return response.data;
  }

  // Messages
  async getMessages(chatId: number): Promise<ApiResponse<Message[]>> {
    const response = await this.api.get(`/api/chats/${chatId}/messages`);
    return response.data;
  }

  async sendMessage(chatId: number, content: string): Promise<ApiResponse> {
    const response = await this.api.post('/api/messages/send', { chat_id: chatId, content });
    return response.data;
  }

  // Users
  async getUsers(): Promise<ApiResponse<User[]>> {
    const response = await this.api.get('/api/users');
    return response.data;
  }

  async getOperators(): Promise<ApiResponse<User[]>> {
    const response = await this.api.get('/api/users/operators');
    return response.data;
  }

  // Groups
  async getGroups(): Promise<ApiResponse<ChatGroup[]>> {
    const response = await this.api.get('/api/groups');
    return response.data;
  }

  // GitLab issues (см. backend api/gitlab.py)
  async getChatGitlabIssues(chatId: number): Promise<any[]> {
    const response = await this.api.get(`/api/chats/${chatId}/gitlab-issues`);
    return response.data || [];
  }

  async getGitlabIssueDetail(issueId: number): Promise<any> {
    const response = await this.api.get(`/api/gitlab-issues/${issueId}/detail`);
    return response.data;
  }

  async updateGitlabIssue(issueId: number, body: {
    title?: string;
    description?: string;
    labels?: string[];
    assignee_id?: number | null;
    milestone_id?: number | null;
    due_date?: string | null;
    issue_type?: 'issue' | 'task' | 'incident' | 'test_case' | null;
    state_event?: 'close' | 'reopen' | null;
  }): Promise<any> {
    const response = await this.api.put(`/api/gitlab-issues/${issueId}`, body);
    return response.data;
  }

  async getGitlabMeta(): Promise<{ members: any[]; milestones: any[]; labels?: any[] }> {
    const response = await this.api.get('/api/gitlab/meta');
    return response.data;
  }

  // AI-разбор обращения (см. backend: POST /api/chats/{chat_id}/ai-analysis)
  async getAiAnalysis(chatId: number): Promise<{ analysis: string; ticket_number: number | null }> {
    const response = await this.api.post(`/api/chats/${chatId}/ai-analysis`);
    return response.data;
  }

  // Expose axios instance for direct access
  get axiosInstance() {
    return this.api;
  }
}

const apiService = new ApiService();

// Export both the service and axios instance
export default apiService.axiosInstance;
export { apiService };
